/**
 * Shared demo-membership cleanup.
 *
 * Before #1354, a real "Explore the Demo Gym" viewer was added to the shared demo
 * org as an `organization_staff` "Demo Viewer" row (plus `athlete_access_cache`
 * rows) so ordinary org-membership RLS would let them browse. That co-mingled a
 * real customer account into the demo org's staff list and once caused the weekly
 * reseed to delete two real owner logins (#1343).
 *
 * #1354 replaces that with a dedicated, read-only `public.demo_org_viewers` grant
 * + is_demo-aware RLS, so a real account never appears in the demo org's
 * staff/parent lists. This helper removes any LEGACY footprint (the old staff row
 * + cache rows) for a user on the shared demo org. It is called by:
 *   - `/api/demo/enter` — to converge pre-#1354 viewers onto the clean grant model.
 *   - `/api/demo/exit`  — to tear a session down (handles legacy + new).
 * For a user who only ever used the new grant model it is a cheap no-op.
 *
 * `clearDemoMembershipFootprint` must be called with a service-role (RLS-bypassing)
 * client. `hasDemoViewerGrant` is safe under either client: `demo_org_viewers` has a
 * read-own SELECT policy for `authenticated`, so the browser client can check its own
 * grant (see `useTeamPermissions` and `canUserAccessAthlete`).
 */

import type { SupabaseClient } from '@supabase/supabase-js';
import { SHARED_DEMO_ORG_ID } from './demoConfig';
import { createLogger } from '@/utils/logger/devLogger';

const logger = createLogger('demo/membership');

/**
 * True when the caller holds a dedicated demo-viewer grant on the shared demo org
 * (#1354). Used by the server-side org/staff access gates so a demo viewer — who
 * no longer has an organization_staff row — keeps the same READ access they had
 * when the old "Demo Viewer" owner row existed. Writes stay blocked by the
 * separate demo write guard (rejectDemoWrite), exactly as before.
 *
 * Fast-paths to false for any non-demo org (the grant only ever exists for the
 * shared demo org), so real-org access checks pay no extra query.
 */
export async function hasDemoViewerGrant(
  organizationId: string,
  userId: string,
  supabase: SupabaseClient,
): Promise<boolean> {
  if (organizationId !== SHARED_DEMO_ORG_ID) return false;
  const { data, error } = await supabase
    .from('demo_org_viewers')
    .select('demo_org_id')
    .eq('auth_user_id', userId)
    .eq('demo_org_id', organizationId)
    .maybeSingle();
  if (error) {
    logger.warn('Failed to check demo-viewer grant (treating as no grant)', { userId, error });
    return false;
  }
  return !!data;
}

export async function clearDemoMembershipFootprint(
  supabase: SupabaseClient,
  userId: string,
): Promise<void> {
  // 1) Legacy organization_staff "Demo Viewer" row (pre-#1354). New viewers have none.
  const { data: staffRow } = await supabase
    .from('organization_staff')
    .select('id')
    .eq('organization_id', SHARED_DEMO_ORG_ID)
    .eq('auth_user_id', userId)
    .maybeSingle();

  if (staffRow?.id) {
    const staffId = staffRow.id;

    // Nullify FK references (NO ACTION constraints) the viewer's owner seat could
    // have created while exploring, so the staff-row delete is never blocked.
    // Safe to clear — the demo data persists for everyone else.
    const fkCleanups = [
      supabase.from('teams').update({ coach_id: null }).eq('coach_id', staffId),
      supabase.from('tryout_registrations').update({ evaluated_by: null }).eq('evaluated_by', staffId),
      supabase.from('tryout_evaluation_history').update({ evaluated_by: null }).eq('evaluated_by', staffId),
      supabase.from('tryout_events').update({ placement_reveal_sent_by: null }).eq('placement_reveal_sent_by', staffId),
      supabase.from('trial_bookings').update({ created_by: null }).eq('created_by', staffId),
      supabase.from('program_public_settings').update({ created_by: null }).eq('created_by', staffId),
      supabase.from('enrollment_cancellations').update({ processed_by: null }).eq('processed_by', staffId),
      supabase.from('team_formation_scenarios').update({ reveal_sent_by: null }).eq('reveal_sent_by', staffId),
      supabase.from('competition_team_results').update({ created_by: null }).eq('created_by', staffId),
      supabase.from('competition_media').update({ uploaded_by: null }).eq('uploaded_by', staffId),
      supabase.from('competitions').update({ created_by: null }).eq('created_by', staffId),
      supabase.from('competition_day_schedules').update({ created_by: null }).eq('created_by', staffId),
      supabase.from('competition_day_schedules').update({ published_by: null }).eq('published_by', staffId),
      supabase.from('organization_staff').update({ reports_to_staff_id: null }).eq('reports_to_staff_id', staffId),
    ];
    const fkResults = await Promise.all(fkCleanups);
    const fkErrors = fkResults.filter((r) => r.error);
    if (fkErrors.length > 0) {
      logger.warn('Legacy demo staff FK cleanups had errors (non-fatal)', {
        userId,
        errors: fkErrors.map((r) => r.error?.message),
      });
    }

    const { error: deleteError } = await supabase
      .from('organization_staff')
      .delete()
      .eq('id', staffId);
    if (deleteError) {
      logger.warn('Failed to remove legacy demo staff row (non-fatal)', { userId, staffId, deleteError });
    } else {
      logger.info('Removed legacy demo staff row (converged to #1354 demo-viewer grant)', { userId });
    }
  }

  // 2) Legacy athlete_access_cache rows for this user on the demo org. New viewers
  //    read demo athletes via is_demo-aware RLS, so none are written going forward.
  await supabase
    .from('athlete_access_cache')
    .delete()
    .eq('organization_id', SHARED_DEMO_ORG_ID)
    .eq('auth_user_id', userId);
}
